import { HelpFab, HelpScopeProvider, ModuleOnboardingBanner } from '@/components/help'
import { SiteSwitcher } from '@/components/warehouse/SiteSwitcher'
import { FADE, pageVariants } from '@/lib/motion'
import { useUIStore } from '@/stores/ui.store'
import { useRouterState } from '@tanstack/react-router'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { type ReactNode, useEffect } from 'react'
import { AnnouncementsBanner } from './AnnouncementsBanner'
import { Breadcrumbs } from './Breadcrumbs'
import { CommandPalette } from './CommandPalette'
import { DunningBanner } from './DunningBanner'
import { Header } from './Header'
import { ImpersonationBanner } from './ImpersonationBanner'
import { SettingsSidebar } from './SettingsSidebar'
import { Sidebar } from './Sidebar'

export function AppLayout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const reduce = useReducedMotion()
  const density = useUIStore((s) => s.density)

  // Reflect data density on <html> so the global CSS rule can tighten tables.
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density)
  }, [density])

  // On /settings/* the workspace sidebar is swapped out for a settings-only
  // one — same navy/gold tokens, same mobile drawer, but the items become
  // Profil / Sécurité / Documents / etc instead of the Finance modules.
  const isSettings = pathname.startsWith('/settings')
  const isWarehouse = pathname === '/warehouse' || pathname.startsWith('/warehouse/')

  return (
    <HelpScopeProvider>
      <div className="flex min-h-dvh bg-muted text-foreground">
        {isSettings ? <SettingsSidebar /> : <Sidebar />}
        <div className="flex min-w-0 flex-1 flex-col">
          <ImpersonationBanner />
          <Header />
          <AnnouncementsBanner />
          <DunningBanner />
          <ModuleOnboardingBanner />
          <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 lg:px-8">
            {isWarehouse ? (
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <Breadcrumbs />
                <SiteSwitcher />
              </div>
            ) : (
              <Breadcrumbs />
            )}
            {reduce ? (
              children
            ) : (
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={pathname}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={FADE}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            )}
          </main>
        </div>
        <HelpFab />
        <CommandPalette />
      </div>
    </HelpScopeProvider>
  )
}
